import React, { useEffect, useState, useMemo } from 'react';
import { MdQrCode2, MdSearch, MdSelectAll, MdClear, MdPictureAsPdf, MdRefresh, MdFilterList } from 'react-icons/md';
import { userApi, departmentApi, locationApi } from '../../../api/adminapi';
import { generateUserCardsPDF } from '../../../utils/pdfUtils';
import './BulkService.css';

function BulkService({ role }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  // Filter states
  const [generateFilters, setGenerateFilters] = useState({ department: '', location: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterLocation, setFilterLocation] = useState('');

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Dropdown options
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Success popup
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch departments and locations on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const response = await departmentApi.getActiveCodes();
        const departments = Array.isArray(response) ? response :
                           (response?.data && Array.isArray(response.data) ? response.data : []);
        setDepartmentOptions(departments);
      } catch (error) {
        console.error('Error fetching departments:', error);
        setDepartmentOptions([]);
      } finally {
        setLoadingDepartments(false);
      }
    };

    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await locationApi.getAllActiveLocationNames();
        const locations = Array.isArray(response) ? response :
                         (response?.data && Array.isArray(response.data) ? response.data : []);
        setLocationOptions(locations);
      } catch (error) {
        console.error('Error fetching locations:', error);
        setLocationOptions([]);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchDepartments();
    fetchLocations();
  }, []);

  // Load users with QR on mount
  useEffect(() => {
    loadUsersWithQR();
  }, []);

  const loadUsersWithQR = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await userApi.getUsersWithQR();

      let usersData = [];
      if (Array.isArray(data)) {
        usersData = data;
      } else if (data?.data && Array.isArray(data.data)) {
        usersData = data.data;
      } else if (data?.content && Array.isArray(data.content)) {
        usersData = data.content;
      } else if (data?.users && Array.isArray(data.users)) {
        usersData = data.users;
      }

      setUsers(usersData);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateQR = async () => {
    if (!generateFilters.department || !generateFilters.location) {
      setError('Please select both department and location to generate QR codes');
      return;
    }

    try {
      setIsGenerating(true);
      setError('');

      const response = await userApi.generateBulkQR(generateFilters.department, generateFilters.location);

      const generatedUsers = Array.isArray(response)
        ? response
        : (Array.isArray(response?.data) ? response.data
          : (Array.isArray(response?.content) ? response.content
            : (Array.isArray(response?.users) ? response.users : [])));

      const defaultMsg = `QR codes generated successfully for ${generateFilters.department} - ${generateFilters.location}. (${generatedUsers.length} users)`;
      setSuccessMessage(response?.message || defaultMsg);
      setShowSuccessPopup(true);

      // Refresh the list
      await loadUsersWithQR();

    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Failed to generate bulk QR codes');
    } finally {
      setIsGenerating(false);
    }
  };

  // Filter users for display
  const filteredUsers = useMemo(() => {
    const query = (searchQuery || '').toLowerCase().trim();
    const dept = (filterDepartment || '').toLowerCase();
    const loc = (filterLocation || '').toLowerCase();

    return (users || []).filter(u => {
      const matchesDept = !dept || (u.department || '').toLowerCase() === dept;
      const matchesLoc = !loc || (u.location || '').toLowerCase() === loc;
      if (!query) return matchesDept && matchesLoc;

      const haystack = [
        u.firstName, u.secondName, u.email, u.empId,
        u.department, u.location, u.phone
      ].map(x => (x || '').toString().toLowerCase()).join(' ');

      return matchesDept && matchesLoc && haystack.includes(query);
    });
  }, [users, searchQuery, filterDepartment, filterLocation]);

  // Selection helpers
  const getUserKey = (u) => u?.id || u?.empId || u?.email;

  const toggleSelect = (user) => {
    const id = getUserKey(user);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const all = new Set(filteredUsers.map(u => getUserKey(u)));
    setSelectedIds(all);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleDownloadPDF = async () => {
    const selectedUsers = filteredUsers.filter(user => selectedIds.has(getUserKey(user)));
    if (selectedUsers.length === 0) {
      alert('Please select at least one user to download as PDF.');
      return;
    }

    try {
      const filename = `qr-cards-${new Date().toISOString().split('T')[0]}.pdf`;
      await generateUserCardsPDF(selectedUsers, filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    setSuccessMessage('');
  };

  // Derive unique departments and locations from current users for table filters
  const tableDepartments = useMemo(() => {
    const depts = Array.from(new Set((users || []).map(u => u.department).filter(Boolean)));
    return depts.sort();
  }, [users]);

  const tableLocations = useMemo(() => {
    const locs = Array.from(new Set((users || []).map(u => u.location).filter(Boolean)));
    return locs.sort();
  }, [users]);

  return (
    <div className="bulk-service-page">
      {/* Page Header */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdQrCode2 className="title-icon" />
            Bulk QR Generation
          </h2>
          <span className="header-subtitle">Generate and manage QR codes for users</span>
        </div>
      </div>

      {/* Generate QR Section */}
      <div className="generate-section">
        <div className="section-header">
          <MdFilterList className="section-icon" />
          <h3>Generate QR Codes</h3>
        </div>
        <div className="generate-controls">
          <select
            className="select-input"
            value={generateFilters.department}
            onChange={(e) => setGenerateFilters(prev => ({ ...prev, department: e.target.value }))}
            disabled={loadingDepartments || isGenerating}
          >
            <option value="">
              {loadingDepartments ? 'Loading...' : 'Select Department'}
            </option>
            {departmentOptions.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>

          <select
            className="select-input"
            value={generateFilters.location}
            onChange={(e) => setGenerateFilters(prev => ({ ...prev, location: e.target.value }))}
            disabled={loadingLocations || isGenerating}
          >
            <option value="">
              {loadingLocations ? 'Loading...' : 'Select Location'}
            </option>
            {locationOptions.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>

          <button
            className="btn-generate"
            onClick={handleGenerateQR}
            disabled={isGenerating || !generateFilters.department || !generateFilters.location}
          >
            <MdQrCode2 />
            {isGenerating ? 'Generating...' : 'Generate QR Codes'}
          </button>

          <button
            className="btn-refresh"
            onClick={loadUsersWithQR}
            disabled={isLoading}
            title="Refresh list"
          >
            <MdRefresh className={isLoading ? 'spinning' : ''} />
          </button>
        </div>
        {error && <div className="error-alert">{error}</div>}
      </div>

      {/* Users Table Section */}
      <div className="users-section">
        <div className="section-toolbar">
          <div className="toolbar-left">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <select
              className="filter-select"
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
            >
              <option value="">All Departments</option>
              {tableDepartments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>

            <select
              className="filter-select"
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
            >
              <option value="">All Locations</option>
              {tableLocations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>

            <button className="btn-text" onClick={() => { setSearchQuery(''); setFilterDepartment(''); setFilterLocation(''); }}>
              Reset
            </button>
          </div>

          <div className="toolbar-right">
            <span className="user-count">
              {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
              {selectedIds.size > 0 && ` (${selectedIds.size} selected)`}
            </span>

            <button className="btn-icon" onClick={selectAll} title="Select all">
              <MdSelectAll />
            </button>

            <button className="btn-icon" onClick={clearSelection} title="Clear selection">
              <MdClear />
            </button>

            <button
              className="btn-download"
              onClick={handleDownloadPDF}
              disabled={selectedIds.size === 0}
            >
              <MdPictureAsPdf />
              Download PDF
            </button>
          </div>
        </div>

        {/* Users Table */}
        {isLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            <MdQrCode2 className="empty-icon" />
            <p>No users with QR codes found</p>
            <span>Generate QR codes using the form above</span>
          </div>
        ) : (
          <div className="users-table">
            <div className="table-header">
              <div className="col-checkbox">
                <input
                  type="checkbox"
                  checked={selectedIds.size === filteredUsers.length && filteredUsers.length > 0}
                  onChange={(e) => e.target.checked ? selectAll() : clearSelection()}
                />
              </div>
              <div className="col-emp-id">Employee ID</div>
              <div className="col-name">Name</div>
              <div className="col-department">Department</div>
              <div className="col-location">Location</div>
              <div className="col-qr">QR Code</div>
            </div>

            <div className="table-body">
              {filteredUsers.map((user) => {
                const key = getUserKey(user);
                const isSelected = selectedIds.has(key);
                const qrSrc = user?.qrCodeBase64 ? `data:image/png;base64,${user.qrCodeBase64}` : null;

                return (
                  <div
                    key={key}
                    className={`table-row ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleSelect(user)}
                  >
                    <div className="col-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(user)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="col-emp-id">
                      <span className="emp-id-badge">{user.empId || 'N/A'}</span>
                    </div>
                    <div className="col-name">
                      <div className="user-name">{user.firstName} {user.secondName}</div>
                      <div className="user-email">{user.email}</div>
                    </div>
                    <div className="col-department">
                      <span className="dept-badge">{user.department || 'N/A'}</span>
                    </div>
                    <div className="col-location">
                      <span className="location-badge">{user.location || 'N/A'}</span>
                    </div>
                    <div className="col-qr">
                      {qrSrc ? (
                        <img src={qrSrc} alt="QR" className="qr-thumbnail" />
                      ) : (
                        <div className="qr-placeholder-small">No QR</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Success Popup Modal */}
      {showSuccessPopup && (
        <div className="modal-overlay" onClick={handleCloseSuccessPopup}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Success!</h3>
              <button className="modal-close-btn" onClick={handleCloseSuccessPopup}>×</button>
            </div>
            <div className="modal-body">
              <div className="success-icon">✓</div>
              <p>{successMessage}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleCloseSuccessPopup}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkService;
